import {
	ActionButton,
	Menu,
	MenuItem,
	MenuTrigger,
	Popover,
	Text,
} from '@react-spectrum/s2';
import AlertTriangle from '@react-spectrum/s2/icons/AlertTriangle';
import More from '@react-spectrum/s2/icons/More';
import OpenIn from '@react-spectrum/s2/icons/OpenIn';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useBridgewayApi } from '@/api/BridgewayApi.ts';
import { getInstagramChannelFromCalendars } from '@/api/ExpressApi';
import type { ExpressAPI } from '@/api/ExpressApi';
import { useExpressApiContext } from '@/api/ExpressApiContext';
import { RestApiError } from '@/api/RestApi';
import { useUserId } from '@/auth/UserIdContext';
import { config } from '@/config/env';
import { getBlobAccessUrl } from '@/hooks/useBlobAccessUrl';
import { useUserSettings } from '@/hooks/useUserSettings';
import { logger } from '@/logger';
import {
	isSameMinute,
	postScheduleIso,
	schedulePublishState,
} from '@/spaces/chat/panels/scheduleUtils';
import {
	ContentDetailStatusCallout,
	type ContentDetailStatusKind,
} from '@/spaces/content/ContentDetailStatusCallout';
import { dismissDeletedArtifact } from '@/spaces/content/contentDetailUtils';
import { DeleteConfirmationWrapper } from '@/spaces/content/DeleteConfirmationWrapper';
import { ExpressSchedulePopoverWrapper } from '@/spaces/content/ExpressSchedulePopoverWrapper';
import { InstagramPostPreview } from '@/spaces/content/InstagramPostPreview.tsx';
import { useArtifactEditChat } from '@/spaces/content/useArtifactEditChat';
import { useScheduleProvenance } from '@/spaces/content/useScheduleProvenance';
import { trackEvent } from '@/utils/fullstory';

import type { InstagramPost } from '../../../../bridge/types/artifact.ts';
import type { Process } from '../../../../bridge/types/process.ts';

interface PostContentDetailProps {
	post: InstagramPost;
	/** The Content space's loaded processes, so a scheduled post can name its campaign. */
	processById?: Map<string, Process>;
	refresh?: () => Promise<void>;
}

// The stored schedule as a real instant. This MUST carry a zone: the schedule popover
// feeds it to `parseAbsoluteToLocal`, which rejects a naive `<on>T<at>` string and falls
// back to the current time inside a silent catch — so a bare wall-clock string made
// Reschedule quietly forget the time the post was already set for, and made the
// repeat-schedule guard below unreachable.
const getScheduledDateTime = (post: InstagramPost): string | undefined =>
	postScheduleIso(post) ?? undefined;

const getDefaultScheduleDraft = (post: InstagramPost) =>
	getScheduledDateTime(post) ?? moment().add(1, 'minute').toISOString();

const sanitizeScheduleDateTimeForExpress = (dateTime: string) => {
	const parsed = moment(dateTime);
	if (!parsed.isValid()) return dateTime;

	const now = moment();
	const clampedToNow = parsed.isBefore(now) ? now : parsed;

	if (clampedToNow.seconds() === 0 && clampedToNow.milliseconds() === 0) {
		return clampedToNow.toISOString();
	}

	const roundedUpToMinute = clampedToNow.clone().startOf('minute');
	if (roundedUpToMinute.isBefore(clampedToNow)) {
		roundedUpToMinute.add(1, 'minute');
	}
	return roundedUpToMinute.toISOString();
};

const PostStateIndicator = ({ error }: { error: string }) => (
	<span className="flex items-center gap-2">
		<AlertTriangle />
		<Text
			styles={style({
				font: 'body',
				fontSize: 'body',
				color: 'body',
			})}
		>
			{error}
		</Text>
	</span>
);

const getPublishErrorDetail = (post: InstagramPost): string | undefined => {
	const { error: err, fail_cause } = post.express_metadata?.publish_error ?? {};

	// Prefer the structured message from Express (e.g. aspect ratio violations)
	if (err && typeof err === 'object' && 'message' in err && err.message)
		return String(err.message);

	// Fall back to a fail_cause-specific message
	if (fail_cause === 'connectionValidation')
		return 'Invalid Instagram connection in Express. Reconnect to Express and reschedule the post';

	// Plain string errors and unknown cases show no detail — the badge label is enough
	return undefined;
};

const formatScheduledAt = (post: InstagramPost): string | null =>
	post.scheduled_at
		? moment(post.scheduled_at).format('MM/DD/YY [at] h:mma')
		: null;

// Answers "when did I schedule this, and where from?" on the detail view. Every
// clause is optional and dropped on its own: a post scheduled before this field
// existed has no time, a post that belongs to no campaign has no campaign, and a
// chat this client has never loaded has no title. So an older post reads exactly
// as it did before. The caller must not render this at all when every clause is
// absent, because the callout draws a divider around any detail node it is given.
//
// The campaign is named before the chat because it answers the owner's question
// more directly: two posts scheduled in one activation share a campaign, so the
// campaign says which piece of work this post came from.
const ScheduleProvenanceDetail = ({
	scheduledAt,
	campaignTitle,
	chatTitle,
	openChat,
}: {
	scheduledAt: string | null;
	campaignTitle: string | null;
	chatTitle: string | null;
	openChat: (() => void) | null;
}) => (
	<span className="inline-flex flex-wrap items-center gap-1">
		<Text>{scheduledAt ? `Scheduled on ${scheduledAt}` : 'Scheduled'}</Text>
		{campaignTitle && <Text>{`for "${campaignTitle}"`}</Text>}
		{chatTitle && (
			<>
				<Text>from</Text>
				{openChat ? (
					<button
						type="button"
						className="underline underline-offset-2"
						onClick={openChat}
					>
						{`"${chatTitle}"`}
					</button>
				) : (
					<Text>{`"${chatTitle}"`}</Text>
				)}
			</>
		)}
	</span>
);

const PAST_DUE_DETAIL =
	'We have not been able to confirm this post went live on Instagram. You can' +
	' reschedule it to try again.';

const UNCONFIRMED_DETAIL =
	'Adobe Express is publishing this post. Bridgeway checks when you open the' +
	' app, and it has not confirmed this post yet.';

const ScheduledStatusCallout = ({
	post,
	isScheduling,
	scheduleDraft,
	processById,
}: {
	post: InstagramPost;
	isScheduling: boolean;
	scheduleDraft: string;
	processById?: Map<string, Process>;
}) => {
	const { campaignTitle, chatTitle, openChat } = useScheduleProvenance(
		post,
		processById,
	);
	const scheduledAt = formatScheduledAt(post);
	const hasProvenance = !!scheduledAt || !!campaignTitle || !!chatTitle;
	const isProcessing = isScheduling || post.status === 'processing';
	// The status alone, with no Express post id required. A post whose Express
	// create answered with no id still reads `scheduled` in Bridgeway's own row.
	// That post needs this callout most: nothing will publish it, so the
	// reschedule prompt is the only way out. Requiring the id hid the callout
	// from exactly that post, while the tile beside it read "Confirming". The
	// Express link in `trailing` already disables itself without an id.
	const isScheduled = post.status === 'scheduled';
	const isPublished = post.status === 'published';
	const isFailed = post.status === 'failed';

	if (!isProcessing && !isScheduled && !isPublished && !isFailed) return null;

	const expressPostId = post.express_metadata?.post_id;

	let status: ContentDetailStatusKind;
	let label: string;

	if (isFailed) {
		status = 'failed';
		label = 'An error occurred publishing the post';
	} else {
		const displayScheduledDateTime = isScheduling
			? scheduleDraft
			: getScheduledDateTime(post);
		if (!displayScheduledDateTime) return null;

		const dateTime = moment(displayScheduledDateTime).format(
			'MM/DD/YY [at] h:mma',
		);
		if (isProcessing) {
			status = 'processing';
			label = `Scheduling for ${dateTime}`;
		} else if (isPublished) {
			status = 'published';
			label = `Posted at ${dateTime}`;
		} else {
			switch (
				schedulePublishState(displayScheduledDateTime, post.express_metadata)
			) {
				case 'past_due':
					status = 'past_due';
					label = `Past due — was scheduled for ${dateTime}`;
					break;
				case 'unconfirmed':
					status = 'unconfirmed';
					label = `Publishing — scheduled for ${dateTime}`;
					break;
				default:
					status = 'scheduled';
					label = `Scheduled for ${dateTime}`;
			}
		}
	}

	return (
		<ContentDetailStatusCallout
			status={status}
			label={label}
			progressAriaLabel="Scheduling"
			detail={
				isFailed ? (
					getPublishErrorDetail(post)
				) : status === 'past_due' ? (
					PAST_DUE_DETAIL
				) : status === 'unconfirmed' ? (
					UNCONFIRMED_DETAIL
				) : status === 'scheduled' && hasProvenance ? (
					<ScheduleProvenanceDetail
						scheduledAt={scheduledAt}
						campaignTitle={campaignTitle}
						chatTitle={chatTitle}
						openChat={openChat}
					/>
				) : undefined
			}
			trailing={
				<ActionButton
					isQuiet
					isDisabled={!expressPostId}
					aria-label="Open this post in Adobe Express (opens in a new tab)"
					onPress={() => {
						trackEvent('Express Schedule Link Clicked', { post_id: post.id });
						window.open(
							`${config.EXPRESS_WEB_HOST}/schedule?postId=${expressPostId}`,
							'_blank',
							'noopener,noreferrer',
						);
					}}
				>
					<OpenIn />
					<Text>Open in Adobe Express</Text>
				</ActionButton>
			}
		/>
	);
};

const EXPRESS_POST_UNCONFIRMED_MESSAGE =
	'Adobe Express no longer recognises the scheduled copy of this post, and we cannot confirm it is gone. Please check the post in Adobe Express, then try again.';

// Mirrors NOT_FOUND_MAX_ATTEMPTS and NOT_FOUND_PAST_DUE_GRACE in beacon's
// api/routes/instagram/resolve_media_ids.py. Keep both in step: a looser value
// on either constant here would create a replacement for a post the server
// still considers live. isSchedulePastDue's 15-minute grace is for the UI's
// "Past due" label, not this rule, and is deliberately not reused here.
const NOT_FOUND_MAX_ATTEMPTS = 3;
const NOT_FOUND_PAST_DUE_GRACE_MS = 60 * 60 * 1000;

// True only when the server has retired this Express post.
//
// A 404 from Express is never proof on its own — Express has answered 404 for a
// post that later published on time, which is why the delete path below also
// refuses to read stored state as proof. The server watches each post instead
// and retires it only after NOT_FOUND_MAX_ATTEMPTS separate "not found"
// answers AND its own schedule has passed NOT_FOUND_PAST_DUE_GRACE_MS. That is
// the one signal strong enough to license a replacement, because creating one
// for a post Express still holds would publish the customer's content twice.
const expressPostIsProvenGone = (post: InstagramPost): boolean => {
	const resolution = post.express_metadata?.media_id_resolution;
	if (resolution?.status !== 'not_found') return false;
	if ((resolution.attempts ?? 0) < NOT_FOUND_MAX_ATTEMPTS) return false;
	const due = postScheduleIso(post);
	// No schedule to wait out (an on-activation post) means the attempt count
	// alone decides, same as the server's _is_retired.
	if (!due) return true;
	return new Date(due).getTime() + NOT_FOUND_PAST_DUE_GRACE_MS < Date.now();
};

// Asks Express to drop its copy of the post, and treats "already gone" as done.
//
// Always send the request. Stored state, such as express_metadata.media_id_resolution,
// is never proof that the Express copy is gone: Express has answered 404 for a post
// that later published on time. Skipping the request on a stale marker would let
// Express publish a post the owner just deleted here.
//
// `isQueuedInExpress` says whether Express is still going to publish this post.
// It decides what a failed removal means, and the answer is the same whether the
// client is missing or the request fails. A queued post needs the removal to
// succeed, because nothing else stops the publish. A post Express already sent
// needs only cleanup, so a failure there must not block the local write.
const removeExpressPost = async (
	expressApi: ExpressAPI | null,
	post: InstagramPost,
	isQueuedInExpress: boolean,
): Promise<void> => {
	const expressPostId = post.express_metadata?.post_id;
	if (!expressPostId) return;
	if (!expressApi) {
		if (isQueuedInExpress) {
			throw new Error(
				`Express is unavailable, so post ${expressPostId} was not removed`,
			);
		}
		// Express already published this post, so the removal is only cleanup.
		// Blocking the local write here would strand the tile for no gain.
		return;
	}
	try {
		await expressApi.deletePostFromExpress(expressPostId);
	} catch (error) {
		// A 404 right now is the end state the caller wants, so let the local write
		// run. Express no longer holds the post.
		if (error instanceof RestApiError && error.status === 404) {
			logger.info('Express post was already gone (404); continuing', {
				post_id: post.id,
				express_post_id: expressPostId,
			});
			return;
		}
		// An outage answers with a raw fetch rejection, not a 404. A queued post
		// must still block the delete. If it did not, Express would publish a post
		// the owner removed here. Express already sent a published post, so no
		// publish is left to stop. Report the failed cleanup, then let the local
		// write run.
		if (!isQueuedInExpress) {
			logger.error('Express cleanup failed for an already-sent post', {
				post_id: post.id,
				express_post_id: expressPostId,
				// The logger sends meta through JSON.stringify, which turns an Error
				// into `{}`. So the reason has to travel as text.
				reason: error instanceof Error ? error.message : String(error),
			});
			return;
		}
		throw error;
	}
};

export const PostContentDetail = ({
	post,
	processById,
	refresh,
}: PostContentDetailProps) => {
	const bridgeApi = useBridgeApi();
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();
	const { expressApi, hasValidInstagramConnection, beginExpressConnection } =
		useExpressApiContext();
	const { settings } = useUserSettings();
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	const [isScheduling, setIsScheduling] = useState(false);
	const [isUnscheduling, setIsUnscheduling] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [mutationError, setMutationError] = useState<string | null>(null);
	const [scheduleDraft, setScheduleDraft] = useState(() =>
		getDefaultScheduleDraft(post),
	);
	const [showScheduleEditor, setShowScheduleEditor] = useState(false);
	// Holds the already-scheduled time, formatted, while the popover asks whether
	// the owner really means to schedule the same post for the same minute again.
	const [duplicateConfirm, setDuplicateConfirm] = useState<string | null>(null);
	const state = post.status ?? 'draft';
	// Delete and Unschedule both end in a PATCH, and the unschedule PATCH writes
	// 'draft'. A 404 from Express used to stop the second press, so the two never
	// both reached the local write. `removeExpressPost` now treats that 404 as
	// done, so only this ref stops an unschedule from returning a deleted post to
	// the Content grid.
	//
	// It holds the post id, not a boolean, because a URL change swaps one post for
	// another without unmounting this component. It is a ref, not state, because
	// the running unschedule has to see a delete that starts after its own render.
	const deletingPostIdRef = useRef<string | null>(null);
	const previousHasValidInstagramConnectionRef = useRef(
		hasValidInstagramConnection,
	);

	useEffect(() => {
		const hasJustConnected =
			!previousHasValidInstagramConnectionRef.current &&
			hasValidInstagramConnection;
		if (hasJustConnected) {
			setMutationError(null);
			setScheduleDraft(getDefaultScheduleDraft(post));
			setShowScheduleEditor(true);
		}

		previousHasValidInstagramConnectionRef.current =
			hasValidInstagramConnection;
	}, [hasValidInstagramConnection, post]);

	const patchPost = async (
		updates: Partial<
			Pick<
				InstagramPost,
				'status' | 'content' | 'schedule' | 'express_metadata' | 'scheduled_at'
			>
		>,
	) => {
		if (!userId) {
			throw new Error('Missing user id');
		}

		const payload: Record<string, unknown> = {};
		if (updates.status) {
			payload.status = updates.status;
		}
		if (updates.content) {
			payload.content = {
				...post.content,
				...updates.content,
			};
		}
		if (updates.schedule !== undefined) {
			payload.schedule = updates.schedule;
		}
		if (updates.scheduled_at !== undefined) {
			payload.scheduled_at = updates.scheduled_at;
		}
		if (updates.express_metadata !== undefined) {
			payload.express_metadata = updates.express_metadata;
		}

		await bridgewayApi.patch(
			`/api/user/${userId}/artifacts/${post.id}`,
			payload,
		);
	};

	const handleEditPost = useArtifactEditChat(post);

	const parseScheduleDraftToAbsolute = (dateTime: string) => {
		const m = moment(dateTime);
		return {
			kind: 'absolute' as const,
			on: m.format('YYYY-MM-DD'),
			at: m.format('HH:mm:ss'),
		};
	};

	const handleConfirmSchedule = async (force = false) => {
		if (!scheduleDraft) {
			setMutationError('Please choose a date and time.');
			return;
		}
		let sanitizedScheduleDraft =
			sanitizeScheduleDateTimeForExpress(scheduleDraft);

		// Scheduling a post for the minute it is already scheduled for repeats
		// work that is already done: it re-uploads every slide and writes the same
		// row to Express again. Ask first rather than dropping the request, so a
		// deliberate reschedule to a different time still goes straight through.
		// Compare against express_metadata.publish_date, not post.schedule: that
		// is the actual committed record of what Express holds, and it is the
		// same field ProcessSchedulePanel's equivalent guard uses, so the two
		// surfaces agree on what counts as a duplicate.
		const committedScheduledDateTime = post.express_metadata?.publish_date;
		if (
			!force &&
			post.status === 'scheduled' &&
			committedScheduledDateTime &&
			isSameMinute(committedScheduledDateTime, sanitizedScheduleDraft)
		) {
			setDuplicateConfirm(
				moment(committedScheduledDateTime).format('MM/DD/YY [at] h:mma'),
			);
			return;
		}

		setDuplicateConfirm(null);
		setScheduleDraft(sanitizedScheduleDraft);
		setIsScheduling(true);
		setShowScheduleEditor(false);
		setMutationError(null);
		try {
			if (!expressApi) {
				throw new Error('Express API is not ready');
			}
			const channelInfo = getInstagramChannelFromCalendars(
				settings?.express_connection?.calendar,
			);
			if (!channelInfo) {
				throw new Error('Missing Express connection details');
			}

			const slideUrls = post.content.image_urls ?? [];
			if (slideUrls.length === 0) {
				throw new Error('Post has no images to schedule');
			}
			const cachedAttachmentIds = post.express_metadata?.attachment_ids ?? [];
			const cachedUploadedUrls =
				post.express_metadata?.image_urls_at_upload ?? [];

			const mediaIds = await Promise.all(
				slideUrls.map(async (slideUrl, i) => {
					const cachedId = cachedAttachmentIds[i];
					const cachedUrl = cachedUploadedUrls[i];
					if (cachedId && cachedUrl === slideUrl) {
						return cachedId;
					}
					const accessUrl = await getBlobAccessUrl(slideUrl, userId);
					const upload = await expressApi.createMediaWithImageUrl(
						accessUrl,
						channelInfo.calendarId,
					);
					if (!upload.success || !upload.mediaId) {
						throw new Error(
							upload.error || `Failed to upload media for slide ${i + 1}`,
						);
					}
					return upload.mediaId;
				}),
			);

			let existing_express_post_id = post.express_metadata?.post_id;
			if (existing_express_post_id) {
				try {
					await expressApi.updatePost(existing_express_post_id, {
						caption: post.content.caption,
						channelId: channelInfo.channelId,
						attachmentIds: mediaIds,
						publishDate: sanitizedScheduleDraft,
						calendarId: channelInfo.calendarId,
						publishType: 'schedule',
					});
				} catch (error) {
					if (!(error instanceof RestApiError && error.status === 404)) {
						throw error;
					}
					if (!expressPostIsProvenGone(post)) {
						logger.warn('Express 404 on reschedule with no proof of loss', {
							post_id: post.id,
							express_post_id: existing_express_post_id,
						});
						throw new Error(EXPRESS_POST_UNCONFIRMED_MESSAGE);
					}
					// The server watched this post and answered "not found" enough times
					// to retire it, so a replacement cannot duplicate a live post. The
					// 404 alone never licenses this: Express has answered 404 for a post
					// that later published on time.
					//
					// Re-clamp against the clock. The slide re-upload above can outlast
					// the chosen minute, and Express refuses a date that has passed. The
					// patch below reads the same value, so the stored record and Express
					// cannot disagree about when the post goes out.
					sanitizedScheduleDraft = sanitizeScheduleDateTimeForExpress(
						sanitizedScheduleDraft,
					);
					const replacement = await expressApi.createPost({
						calendarId: channelInfo.calendarId,
						caption: post.content.caption,
						channelId: channelInfo.channelId,
						mediaIds,
						publishDate: sanitizedScheduleDraft,
						publishType: 'schedule',
					});
					existing_express_post_id = replacement[0]?.id;
					if (!existing_express_post_id) {
						throw new Error(EXPRESS_POST_UNCONFIRMED_MESSAGE);
					}
				}
			} else {
				const created = await expressApi.createPost({
					calendarId: channelInfo.calendarId,
					caption: post.content.caption,
					channelId: channelInfo.channelId,
					mediaIds,
					publishDate: sanitizedScheduleDraft,
					publishType: 'schedule',
				});
				existing_express_post_id = created[0]?.id;
			}

			await patchPost({
				status: 'scheduled',
				schedule: parseScheduleDraftToAbsolute(sanitizedScheduleDraft),
				scheduled_at: new Date().toISOString(),
				express_metadata: existing_express_post_id
					? {
							post_id: existing_express_post_id,
							attachment_ids: mediaIds,
							image_urls_at_upload: slideUrls,
							// The artifact PATCH replaces express_metadata whole rather than
							// merging its keys, so every write has to name the target it just
							// sent to Express. Leaving this out erases the date the
							// repeat-schedule guard reads, and the guard then treats the next
							// press as new work.
							publish_date: sanitizedScheduleDraft,
						}
					: undefined,
			});
			trackEvent('Content Post Scheduled', { post_id: post.id });
			setShowScheduleEditor(false);
			await refresh?.();
		} catch (error) {
			// Only messages this file wrote reach the customer. An upstream message
			// can carry an artifact id or a raw upstream body.
			const known =
				error instanceof Error &&
				error.message === EXPRESS_POST_UNCONFIRMED_MESSAGE
					? error.message
					: 'Failed to schedule post. Please try again.';
			setMutationError(known);
			logger.error('Failed to schedule post', error);
		} finally {
			setIsScheduling(false);
		}
	};

	const handleReschedule = () => {
		setMutationError(null);
		setDuplicateConfirm(null);
		setShowScheduleEditor(true);
		setScheduleDraft(getDefaultScheduleDraft(post));
	};

	const handleUnschedulePost = async () => {
		if (deletingPostIdRef.current === post.id) return;
		setIsUnscheduling(true);
		setMutationError(null);
		try {
			await removeExpressPost(expressApi, post, state === 'scheduled');
			// The owner pressed Delete while this removal was in flight. That press
			// is the later intent, and it writes 'cancelled'. Writing 'draft' now
			// would undo it with no banner to say so.
			if (deletingPostIdRef.current === post.id) return;
			// An explicit null, not undefined: the server replaces this field
			// wholesale, so null is what clears the Express pointer. Undefined is
			// dropped before the request and leaves the stale object in place.
			await patchPost({
				status: 'draft',
				schedule: null,
				scheduled_at: null,
				express_metadata: null,
			});
			trackEvent('Content Post Unscheduled', { post_id: post.id });
			await refresh?.();
		} catch (error) {
			setMutationError('Failed to unschedule post. Please try again.');
			logger.error('Failed to unschedule post', error);
		} finally {
			setIsUnscheduling(false);
		}
	};

	const handleDeletePost = async () => {
		deletingPostIdRef.current = post.id;
		setIsDeleting(true);
		setMutationError(null);
		try {
			if (state === 'scheduled' || state === 'published') {
				await removeExpressPost(expressApi, post, state === 'scheduled');
			}
			await patchPost({ status: 'cancelled' });
			trackEvent('Content Post Deleted', { post_id: post.id });
			setShowDeleteConfirmation(false);
			await refresh?.();
			dismissDeletedArtifact(bridgeApi, post.id);
		} catch (error) {
			// The post is still scheduled, so an unschedule is a valid next press.
			deletingPostIdRef.current = null;
			setMutationError('Failed to delete post. Please try again.');
			logger.error('Failed to delete post', error);
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<div className="flex h-full min-h-0 flex-col gap-6">
			<div className="flex items-center gap-3">
				<div className="min-w-0 flex-1">
					<ScheduledStatusCallout
						post={post}
						isScheduling={isScheduling}
						scheduleDraft={scheduleDraft}
						processById={processById}
					/>
				</div>
				<div className="flex shrink-0 items-center gap-4">
					{mutationError && <PostStateIndicator error={mutationError} />}
					<MenuTrigger>
						<ExpressSchedulePopoverWrapper
							isOpen={showScheduleEditor}
							hasValidInstagramConnection={hasValidInstagramConnection}
							beginExpressConnection={beginExpressConnection}
							scheduleDraft={scheduleDraft}
							onScheduleDraftChange={setScheduleDraft}
							onCancel={() => {
								setDuplicateConfirm(null);
								setShowScheduleEditor(false);
							}}
							onConfirm={() => void handleConfirmSchedule()}
							duplicateScheduleTime={duplicateConfirm}
							onConfirmDuplicate={() => void handleConfirmSchedule(true)}
						>
							<DeleteConfirmationWrapper
								onConfirm={handleDeletePost}
								onCancel={() => setShowDeleteConfirmation(false)}
								isOpen={showDeleteConfirmation}
							>
								<ActionButton isQuiet aria-label="More options">
									<More />
								</ActionButton>
							</DeleteConfirmationWrapper>
						</ExpressSchedulePopoverWrapper>
						<Popover>
							<Menu>
								{state === 'scheduled' && (
									<>
										<MenuItem id="edit-scheduled" onPress={handleEditPost}>
											<Text>Edit Post</Text>
										</MenuItem>
										<MenuItem
											id="reschedule"
											onPress={handleReschedule}
											isDisabled={!hasValidInstagramConnection || isScheduling}
										>
											<Text>Reschedule</Text>
										</MenuItem>
										<MenuItem
											id="unschedule"
											onPress={handleUnschedulePost}
											isDisabled={
												!hasValidInstagramConnection ||
												isUnscheduling ||
												isDeleting
											}
										>
											<Text>Unschedule</Text>
										</MenuItem>
									</>
								)}
								{(state === 'draft' || state === 'failed') && (
									<>
										<MenuItem id="edit-draft" onPress={handleEditPost}>
											<Text>Edit Draft</Text>
										</MenuItem>
										<MenuItem
											id="schedule-draft"
											onPress={handleReschedule}
											isDisabled={isScheduling}
										>
											<Text>Schedule</Text>
										</MenuItem>
									</>
								)}
								{state === 'published' && (
									<MenuItem id="edit-posted" onPress={handleEditPost}>
										<Text>Edit Post</Text>
									</MenuItem>
								)}
								<MenuItem
									id="delete"
									onPress={() => setShowDeleteConfirmation(true)}
									isDisabled={isDeleting}
								>
									<Text>Delete</Text>
								</MenuItem>
							</Menu>
						</Popover>
					</MenuTrigger>
				</div>
			</div>

			<div className="flex min-h-0 flex-1">
				<InstagramPostPreview
					posts={[post]}
					selectedPostIndex={0}
					contentPanel={
						<div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
							<div className="flex min-h-0 flex-1 flex-col gap-3">
								<Text
									styles={style({
										font: 'body',
										color: 'body',
										fontSize: 'body',
										fontWeight: 'bold',
									})}
								>
									Caption
								</Text>
								<div className="min-h-0 flex-1 overflow-y-auto">
									<Text
										styles={style({
											font: 'body',
											color: 'body',
											fontSize: 'body',
											whiteSpace: 'pre-wrap',
										})}
									>
										{post.content.caption || 'No caption'}
									</Text>
								</div>
							</div>
						</div>
					}
				/>
			</div>
		</div>
	);
};
